import type { CreateResearchRun, ResearchRunStreamEvent, RetryResearchRun } from './generated/api';
import { ApiError, scopedFetch } from './mutator';

const eventKinds = new Set<ResearchRunStreamEvent['event']>([
  'started',
  'delta',
  'evidence',
  'completed',
  'error',
  'cancelled',
  'keepalive'
]);

function eventFromJson(value: unknown): ResearchRunStreamEvent {
  if (!value || typeof value !== 'object') throw new ApiError('Research stream returned an invalid event.', 502, 'RESEARCH_STREAM_INVALID');
  const event = value as Partial<ResearchRunStreamEvent>;
  if (
    typeof event.event !== 'string' ||
    !eventKinds.has(event.event as ResearchRunStreamEvent['event']) ||
    typeof event.runId !== 'string' ||
    !Number.isInteger(event.sequence) ||
    Number(event.sequence) < 0
  ) {
    throw new ApiError('Research stream returned an invalid event.', 502, 'RESEARCH_STREAM_INVALID');
  }
  return event as ResearchRunStreamEvent;
}

async function responseError(response: Response): Promise<ApiError> {
  const contentType = response.headers.get('content-type') ?? '';
  const body = contentType.includes('application/json')
    ? await response.json().catch(() => undefined) as { error?: string; code?: string } | undefined
    : undefined;
  if (body?.code === 'SCOPE_NOT_FOUND' && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('cdf:access-invalidated'));
  }
  return new ApiError(body?.error ?? `Request failed with status ${response.status}`, response.status, body?.code);
}

async function* streamEvents(url: string, body: unknown, signal?: AbortSignal): AsyncGenerator<ResearchRunStreamEvent> {
  const response = await scopedFetch(url, {
    method: 'POST',
    headers: { Accept: 'text/event-stream' },
    body: JSON.stringify(body),
    signal
  });
  if (!response.ok) throw await responseError(response);
  if (!(response.headers.get('content-type') ?? '').includes('text/event-stream')) {
    throw new ApiError('Research stream returned an unsupported response.', 502, 'RESEARCH_STREAM_CONTENT_TYPE');
  }
  if (!response.body) throw new ApiError('Research stream returned no response body.', 502, 'RESEARCH_STREAM_EMPTY');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (true) {
      const { done, value } = await reader.read();
      buffer += decoder.decode(value, { stream: !done }).replace(/\r\n/g, '\n');
      let boundary = buffer.indexOf('\n\n');
      while (boundary >= 0) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const data = frame.split('\n').filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).trimStart()).join('\n');
        if (data) {
          let parsed: unknown;
          try {
            parsed = JSON.parse(data);
          } catch {
            throw new ApiError('Research stream returned malformed JSON.', 502, 'RESEARCH_STREAM_INVALID');
          }
          yield eventFromJson(parsed);
        }
        boundary = buffer.indexOf('\n\n');
      }
      if (done) break;
    }
    if (buffer.trim()) throw new ApiError('Research stream ended with an incomplete event.', 502, 'RESEARCH_STREAM_TRUNCATED');
  } finally {
    reader.releaseLock();
  }
}

export function createResearchRunEventStream(
  threadId: string,
  body: CreateResearchRun,
  signal?: AbortSignal
) {
  return streamEvents(`/api/research/threads/${encodeURIComponent(threadId)}/runs`, body, signal);
}

export function createResearchRetryEventStream(
  threadId: string,
  runId: string,
  body: RetryResearchRun,
  signal?: AbortSignal
) {
  return streamEvents(
    `/api/research/threads/${encodeURIComponent(threadId)}/runs/${encodeURIComponent(runId)}/retry`,
    body,
    signal
  );
}
